import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SteelLinkLogo from '@/components/feature/SteelLinkLogo';
import TranslateButton from '@/components/feature/TranslateButton';

const navItems = [
  { label: 'Home', href: '/' },
  {
    label: 'Construction',
    href: '/products/construction-building',
    children: [
      { label: 'Cold-Rolled Coil (CRC)', href: '/products/item/cold-rolled-coil' },
      { label: 'Hot-Rolled Coil (HRC)', href: '/products/item/hot-rolled-coil' },
      { label: 'Pre-Painted Galvanized Coil', href: '/products/item/pre-painted-galvanized-coil' },
      { label: 'Rebar HRB400', href: '/products/item/rebar-hrb400' },
      { label: 'H-Beam Steel', href: '/products/item/h-beam-steel' },
      { label: 'I-Beam Steel (IPE/IPN)', href: '/products/item/i-beam-steel' },
      { label: 'Wire Rod', href: '/products/item/wire-rod' },
      { label: 'Galvanized Wire', href: '/products/item/galvanized-wire' },
    ],
  },
  {
    label: 'Roofing',
    href: '/products/roofing-cladding',
    children: [
      { label: 'Purlin Gi 2.0', href: '/products/item/purlin-gi' },
      { label: 'Corrugated Roofing Sheet', href: '/products/item/corrugated-roofing-sheet' },
      { label: 'PPGI Sheet', href: '/products/item/ppgi-sheet' },
      { label: 'Galvalume Steel Coil', href: '/products/item/galvalume-steel-coil' },
      { label: 'GI Sheet', href: '/products/item/gi-sheet' },
      { label: 'Sandwich Panel (EPS/PU)', href: '/products/item/sandwich-panel' },
    ],
  },
  {
    label: 'Piping',
    href: '/products/plumbing-piping',
    children: [
      { label: 'ERW Black Pipe', href: '/products/item/erw-black-pipe' },
      { label: 'ERW Galvanized Pipe', href: '/products/item/erw-galvanized-pipe' },
      { label: 'Seamless Pipe', href: '/products/item/seamless-pipe' },
      { label: 'Stainless Steel Pipe', href: '/products/item/stainless-steel-pipe' },
      { label: 'Casing Pipe (API 5CT)', href: '/products/item/casing-pipe-api5ct' },
      { label: 'Pipe Fittings', href: '/products/item/pipe-fittings' },
      { label: 'LSAW Pipe', href: '/products/item/lsaw-pipe' },
    ],
  },
  {
    label: 'Industrial',
    href: '/products/industrial-structural',
    children: [
      { label: 'Steel Plate G3358', href: '/products/item/steel-plate' },
      { label: 'Checkered Plate', href: '/products/item/checkered-plate' },
      { label: 'U-Channel 200mm', href: '/products/item/u-channel' },
      { label: 'Rectangular Tube (RHS)', href: '/products/item/rectangular-tube' },
      { label: 'Angle Iron', href: '/products/item/angle-iron' },
      { label: 'Perforated Sheet', href: '/products/item/perforated-sheet' },
    ],
  },
  {
    label: 'Wire & Mesh',
    href: '/products/wire-mesh',
    children: [
      { label: 'Barbed Wire (GI / PVC)', href: '/products/item/barbed-wire' },
      { label: 'Chain Link Fence', href: '/products/item/chain-link-fence' },
      { label: 'Welded Wire Mesh', href: '/products/item/welded-wire-mesh' },
    ],
  },
];

// Secondary nav items grouped under "More"
const moreItems = [
  { label: 'Shipping', href: '/#shipping', icon: 'ri-ship-line' },
  { label: 'Case Study', href: '/sourcing-case', icon: 'ri-image-2-line' },
  { label: 'About Us', href: '/about', icon: 'ri-information-line' },
  { label: 'FAQ', href: '/faq', icon: 'ri-question-line' },
  { label: 'Contact', href: '/contact', icon: 'ri-mail-line' },
];

// All items for mobile drawer
const allMobileItems = [
  { label: 'Home', href: '/' },
  ...navItems.slice(1),
  ...moreItems.map(({ label, href }) => ({ label, href })),
];

interface NavbarProps {
  onQuoteClick: () => void;
}

export default function Navbar({ onQuoteClick }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleNav = (href: string) => {
    setMobileOpen(false);
    setOpenDropdown(null);
    if (href === '/#shipping') {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          document.getElementById('shipping-section')?.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      } else {
        document.getElementById('shipping-section')?.scrollIntoView({ behavior: 'smooth' });
      }
      return;
    }
    navigate(href);
  };

  const isActive = (href: string) => location.pathname === href || location.pathname.startsWith(href + '/');
  const isMoreActive = moreItems.some((i) => isActive(i.href) && i.href !== '/');

  return (
    <>
      <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        {/* Top announcement bar */}
        <div className="hidden lg:block" style={{ backgroundColor: '#0a2342' }}>
          <div className="max-w-[1420px] mx-auto px-6 py-1.5 flex items-center justify-between text-xs font-semibold text-white">
            <div className="flex items-center gap-6">
              <span><i className="ri-truck-line mr-1" />Fast Dispatch After Deposit</span>
              <span><i className="ri-ship-line mr-1" />FOB Major Chinese Ports</span>
              <span><i className="ri-award-line mr-1" />10+ Years Experience</span>
            </div>
            <div className="flex items-center gap-4">
              <a href="/track" onClick={(e) => { e.preventDefault(); handleNav('/track'); }} className="flex items-center gap-1.5 hover:opacity-80 cursor-pointer">
                <i className="ri-map-pin-time-line" />Track Order
              </a>
              <a href="https://wa.me/8617671262710" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:opacity-80">
                <i className="ri-whatsapp-line" />+86 17671262710
              </a>
            </div>
          </div>
        </div>

        <div className="max-w-[1420px] mx-auto px-4 lg:px-6 flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a href="/" onClick={(e) => { e.preventDefault(); handleNav('/'); }} className="flex-shrink-0 cursor-pointer">
            <SteelLinkLogo variant={scrolled ? 'dark' : 'light'} size="lg" />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-0">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.children ? setOpenDropdown(item.label) : undefined}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <a
                  href={item.href}
                  onClick={(e) => { e.preventDefault(); handleNav(item.href); }}
                  className={`px-2.5 py-2 text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer rounded-md flex items-center gap-0.5 ${
                    isActive(item.href) && item.href !== '/'
                      ? ''
                      : scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                  }`}
                  style={isActive(item.href) && item.href !== '/' ? { color: '#0a2342' } : {}}
                >
                  {item.label}
                  {item.children && <i className="ri-arrow-down-s-line text-xs ml-0.5" />}
                </a>
                {item.children && openDropdown === item.label && (
                  <div className="absolute top-full left-0 bg-white rounded-xl py-2 min-w-[220px] z-50 border border-gray-100 mt-1"
                    style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}>
                    <div className="px-3 pb-1 mb-1 border-b border-gray-100">
                      <a
                        href={item.href}
                        onClick={(e) => { e.preventDefault(); handleNav(item.href); }}
                        className="text-xs font-black uppercase tracking-widest cursor-pointer hover:opacity-80"
                        style={{ color: '#0a2342' }}
                      >
                        View All {item.label} →
                      </a>
                    </div>
                    {item.children.map((child) => (
                      <a
                        key={child.label}
                        href={child.href}
                        onClick={(e) => { e.preventDefault(); handleNav(child.href); }}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:text-white whitespace-nowrap cursor-pointer mx-1 rounded-lg transition-colors"
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#0a2342'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = ''; }}
                      >
                        <i className="ri-arrow-right-s-line text-xs" style={{ color: '#1e5fa8' }} />
                        {child.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* More dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setOpenDropdown('__more__')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <button
                className={`px-2.5 py-2 text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer rounded-md flex items-center gap-0.5 ${
                  isMoreActive ? '' : scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'
                }`}
                style={isMoreActive ? { color: '#0a2342' } : {}}
              >
                More
                <i className={`ri-arrow-down-s-line text-xs ml-0.5 transition-transform duration-200 ${openDropdown === '__more__' ? 'rotate-180' : ''}`} />
              </button>
              {openDropdown === '__more__' && (
                <div className="absolute top-full right-0 bg-white rounded-xl py-2 min-w-[180px] z-50 border border-gray-100 mt-1"
                  style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}>
                  {moreItems.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      onClick={(e) => { e.preventDefault(); handleNav(item.href); }}
                      className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:text-white cursor-pointer mx-1 rounded-lg transition-colors"
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#0a2342'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = ''; }}
                    >
                      <i className={`${item.icon} text-sm flex-shrink-0`} style={{ color: '#1e5fa8' }} />
                      {item.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-1">
            <div className="hidden lg:block">
              <TranslateButton scrolled={scrolled} />
            </div>
            <a
              href="https://wa.me/8617671262710"
              target="_blank"
              rel="noopener noreferrer"
              className={`hidden lg:flex items-center gap-1.5 px-2.5 py-2 rounded-md text-sm font-semibold transition-colors whitespace-nowrap ${
                scrolled ? 'text-gray-600 hover:text-green-600' : 'text-white/80 hover:text-green-400'
              }`}
            >
              <i className="ri-whatsapp-line text-green-500 text-base" />
            </a>
            <button
              onClick={onQuoteClick}
              className="hidden lg:flex items-center gap-1.5 px-4 py-2 text-white text-xs font-black rounded-lg active:scale-95 transition-all cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: '#0a2342' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#0d2d56'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#0a2342'; }}
            >
              <i className="ri-flashlight-line text-xs" />
              Quick Quote
            </button>
            {/* Mobile icons */}
            <a
              href="https://wa.me/8617671262710"
              target="_blank"
              rel="noopener noreferrer"
              className={`lg:hidden p-2 cursor-pointer ${scrolled ? 'text-green-600' : 'text-green-400'}`}
              aria-label="WhatsApp"
            >
              <i className="ri-whatsapp-line text-xl" />
            </a>
            <button
              className={`lg:hidden p-2 cursor-pointer transition-colors ${scrolled ? 'text-gray-800' : 'text-white'}`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <i className={`text-2xl transition-all ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
        <div
          className={`absolute top-0 right-0 h-full w-[85vw] max-w-sm bg-white flex flex-col transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10" style={{ backgroundColor: '#0a2342' }}>
            <SteelLinkLogo variant="light" size="md" />
            <button onClick={() => setMobileOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 cursor-pointer">
              <i className="ri-close-line text-white" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto py-2">
            {allMobileItems.map((item) => {
              const hasChildren = 'children' in item && Array.isArray((item as typeof navItems[0]).children);
              return (
                <div key={item.label}>
                  <button
                    className={`w-full flex items-center justify-between px-5 py-3.5 text-sm font-semibold cursor-pointer transition-colors ${
                      isActive(item.href) && item.href !== '/' ? 'text-white' : 'text-gray-800 hover:bg-gray-50'
                    }`}
                    style={isActive(item.href) && item.href !== '/' ? { backgroundColor: '#0a2342' } : {}}
                    onClick={() => {
                      if (hasChildren) {
                        setOpenDropdown(openDropdown === item.label ? null : item.label);
                      } else {
                        handleNav(item.href);
                      }
                    }}
                  >
                    <span>{item.label}</span>
                    {hasChildren && (
                      <i className={`ri-arrow-down-s-line text-gray-400 transition-transform duration-200 ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                    )}
                  </button>
                  {hasChildren && openDropdown === item.label && (
                    <div className="bg-gray-50 border-t border-b border-gray-100">
                      <a
                        href={item.href}
                        onClick={(e) => { e.preventDefault(); handleNav(item.href); }}
                        className="block px-8 py-2.5 text-xs font-black uppercase tracking-widest cursor-pointer"
                        style={{ color: '#0a2342' }}
                      >
                        View All →
                      </a>
                      {(item as typeof navItems[0]).children?.map((child) => (
                        <a
                          key={child.label}
                          href={child.href}
                          onClick={(e) => { e.preventDefault(); handleNav(child.href); }}
                          className="flex items-center gap-2 px-8 py-2.5 text-sm text-gray-600 cursor-pointer hover:text-white transition-colors"
                          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#0a2342'; (e.currentTarget as HTMLElement).style.color = 'white'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = ''; (e.currentTarget as HTMLElement).style.color = ''; }}
                        >
                          <i className="ri-arrow-right-s-line text-xs flex-shrink-0" style={{ color: '#1e5fa8' }} />
                          {child.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="p-4 border-t border-gray-100 space-y-3 bg-gray-50">
            <div className="flex justify-center">
              <TranslateButton scrolled={true} />
            </div>
            <a
              href="/quote"
              onClick={(e) => { e.preventDefault(); handleNav('/quote'); }}
              className="w-full text-white py-3.5 rounded-xl text-sm font-black cursor-pointer active:scale-95 transition-all flex items-center justify-center"
              style={{ backgroundColor: '#0a2342' }}
            >
              <i className="ri-file-list-3-line mr-2" />Get a Free Quote
            </a>
            <div className="grid grid-cols-2 gap-2">
              <a
                href="https://wa.me/8617671262710"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-green-500 text-white py-3 rounded-xl text-sm font-black hover:bg-green-600 active:scale-95 transition-all"
              >
                <i className="ri-whatsapp-line text-base" />WhatsApp
              </a>
              <a
                href="/track"
                onClick={(e) => { e.preventDefault(); handleNav('/track'); }}
                className="flex items-center justify-center gap-2 text-sm font-black py-3 rounded-xl cursor-pointer active:scale-95 transition-all border-2"
                style={{ borderColor: '#0a2342', color: '#0a2342' }}
              >
                <i className="ri-map-pin-time-line" />Track Order
              </a>
            </div>
            <p className="text-center text-xs text-gray-400">
              <i className="ri-time-line mr-1" style={{ color: '#0a2342' }} />Response within 2 hours
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
